import { encodeQr, qrSvgPath } from '@/lib/qr';

/**
 * Builds the printable credential card face as a self-contained SVG string.
 *
 * A STRING, not JSX, on purpose: the same markup serves the on-screen preview
 * (embedded via an <img> data URI) and the print window (written into a blank
 * document), so what the operator previews is byte-for-byte what prints. Being
 * a pure function it is unit-testable without a DOM.
 *
 * School-neutral by design — no logo, no brand colour — because the card is
 * printed long before any per-institution print tooling exists. The QR encodes
 * ONLY the opaque `crd_…` reference (never PII); when the reference somehow
 * exceeds the tiny encoder's capacity the face falls back to the printed
 * reference string, which remains scannable by keyboard-wedge readers.
 */

export interface CardFaceInput {
  displayName: string;
  institutionName: string;
  credentialPublicId: string;
  /** Localised credential-type label, e.g. "Physical card". */
  credentialTypeLabel: string;
  /** Localised "Expires …" line; empty string prints nothing. */
  expiryLabel: string;
  /** Localised caption under the QR area when no QR could be rendered. */
  noQrNote: string;
}

/** CR80 card proportions (85.6mm x 54mm) at a 4x pixel scale. */
const WIDTH = 342;
const HEIGHT = 216;

export function cardFaceSvg(input: CardFaceInput): string {
  const name = escapeXml(input.displayName);
  const institution = escapeXml(input.institutionName);
  const reference = escapeXml(input.credentialPublicId);
  const typeLabel = escapeXml(input.credentialTypeLabel);
  const expiry = escapeXml(input.expiryLabel);

  const modules = encodeQr(input.credentialPublicId);
  const qrBlock = modules
    ? renderQr(qrSvgPath(modules), modules.length)
    : `<text x="252" y="108" text-anchor="middle" font-size="9" fill="#475569">${escapeXml(
        input.noQrNote,
      )}</text>`;

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" width="${WIDTH}" height="${HEIGHT}" role="img" aria-label="${name}">`,
    `<rect width="${WIDTH}" height="${HEIGHT}" rx="12" fill="#ffffff" stroke="#cbd5e1"/>`,
    `<rect width="${WIDTH}" height="44" rx="12" fill="#0f172a"/>`,
    `<rect y="22" width="${WIDTH}" height="22" fill="#0f172a"/>`,
    `<text x="16" y="28" font-family="sans-serif" font-size="13" font-weight="600" fill="#ffffff">${institution}</text>`,
    `<text x="16" y="86" font-family="sans-serif" font-size="17" font-weight="700" fill="#0f172a">${name}</text>`,
    `<text x="16" y="106" font-family="sans-serif" font-size="10" fill="#475569">${typeLabel}</text>`,
    expiry
      ? `<text x="16" y="122" font-family="sans-serif" font-size="10" fill="#475569">${expiry}</text>`
      : '',
    qrBlock,
    `<text x="16" y="${HEIGHT - 14}" font-family="monospace" font-size="9" fill="#334155">${reference}</text>`,
    `</svg>`,
  ]
    .filter(Boolean)
    .join('');
}

function renderQr(path: string, moduleCount: number): string {
  // 100px square with a quiet zone, right-hand side of the card.
  const box = 108;
  const margin = 4; // quiet zone in modules
  const total = moduleCount + margin * 2;
  const scale = box / total;
  const x = WIDTH - box - 20;
  const y = 54;
  return [
    `<g transform="translate(${x} ${y})">`,
    `<rect width="${box}" height="${box}" fill="#ffffff"/>`,
    `<g transform="scale(${scale.toFixed(4)}) translate(${margin} ${margin})">`,
    `<path d="${path}" fill="#0f172a"/>`,
    `</g></g>`,
  ].join('');
}

/** Data URI for the on-screen <img> preview. */
export function cardFaceDataUri(input: CardFaceInput): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(cardFaceSvg(input))}`;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
